import { motion, useMotionValue } from 'motion/react'
import { type Ref, useRef, useState } from 'react'
import { cn } from '#app/utils/misc'
import {
	useFrameLoop,
	useReducedMotionSafe,
	useSceneClock,
} from '#app/utils/site/scene-clock'
import {
	LENS_SCALE,
	LENS_TIMING,
	type LensKeyframe,
	type LensPhase,
	STATEMENT,
	type StatementRow,
	VERIFIED,
	boundsRelativeTo,
	checksShown,
	clampRowCentres,
	lensKeyframes,
	lensPointAt,
	lensSceneKeyframes,
	lensTransforms,
	lensVisible,
	rowCentresFromBoxes,
	sweepTimeAt,
	verifiedShown,
} from '#app/utils/site/scenes/fake-statement-lens'
import { CheckIcon } from '../../primitives'
import type { SceneProps } from './types'

/**
 * Landlord scene "Fake Statements Look Real": a magnifier sweeps a bank
 * statement, label then amount, row by row. When it is done the statement
 * fades and the TrueTenant card takes its place, each row ticked as it comes
 * straight from the institution through Flinks.
 *
 * The sweep never re-renders React: the row centres are measured once per
 * play, then every frame only writes three transforms through motion values.
 * The coarse phases (lens visible, card shown, checks) come from
 * `useSceneClock`; the timeline is in app/utils/site/scenes/fake-statement-lens.ts.
 */

const FLINKS_LOGO = '/img/site/flinks.png'
const SCENE_KEYFRAMES = lensSceneKeyframes(
	STATEMENT.rows.length,
	VERIFIED.rows.length,
)
const FINAL_PHASE: LensPhase = `check-${VERIFIED.rows.length}`
const ROOT = 'tt-scene-fake-statement-lens'

interface RowElements {
	row: HTMLElement | null
	label: HTMLElement | null
	amount: HTMLElement | null
}

interface Play {
	/** Which 16 s loop the current measurements belong to. */
	loop: number
	frames: LensKeyframe[]
}

export function FakeStatementLensScene({ active }: SceneProps) {
	const reduced = useReducedMotionSafe()
	const { phase } = useSceneClock({
		active,
		keyframes: SCENE_KEYFRAMES,
		loopMs: LENS_TIMING.loopMs,
		finalPhase: FINAL_PHASE,
	})

	const rootRef = useRef<HTMLDivElement>(null)
	const cardRef = useRef<HTMLDivElement>(null)
	const magRef = useRef<HTMLDivElement>(null)
	const rowsRef = useRef<RowElements[]>([])
	const playRef = useRef<Play>({ loop: -1, frames: [] })

	const lensTransform = useMotionValue('translate(0px, 0px)')
	const magTransform = useMotionValue(
		`translate(0px, 0px) scale(${LENS_SCALE})`,
	)
	const handleTransform = useMotionValue('translate(0px, 0px) rotate(-45deg)')

	// Rewind when the tile leaves the viewport (state adjusted during render,
	// as useSceneClock does), so the next entry measures and plays again.
	const [wasActive, setWasActive] = useState(active)
	if (active !== wasActive) {
		setWasActive(active)
		if (!active) playRef.current = { loop: -1, frames: [] }
	}

	const bindRow = (index: number, part: keyof RowElements) => {
		return (element: HTMLElement | null) => {
			const rows = rowsRef.current
			rows[index] ??= { row: null, label: null, amount: null }
			rows[index][part] = element
		}
	}

	/** One layout read per play: row centres and the tile's clip box. */
	const measure = (): LensKeyframe[] => {
		const card = cardRef.current
		const mag = magRef.current
		if (!card) return []
		const cardBox = card.getBoundingClientRect()
		const rows = rowsRef.current.flatMap(({ row, label, amount }) =>
			row && label && amount
				? [
						{
							row: row.getBoundingClientRect(),
							label: label.getBoundingClientRect(),
							amount: amount.getBoundingClientRect(),
						},
					]
				: [],
		)
		const tile = rootRef.current?.parentElement
		const clipBox = tile ? tile.getBoundingClientRect() : cardBox
		if (mag) {
			mag.style.width = `${cardBox.width}px`
			mag.style.height = `${cardBox.height}px`
		}
		return lensKeyframes(
			clampRowCentres(
				rowCentresFromBoxes(cardBox, rows),
				boundsRelativeTo(cardBox, clipBox),
			),
		)
	}

	// Subscribed only while in view and motion is allowed (no idle frames).
	useFrameLoop(active && !reduced, (elapsed) => {
		const play = playRef.current
		const loop = Math.floor(elapsed / LENS_TIMING.loopMs)
		if (loop !== play.loop) {
			play.loop = loop
			play.frames = measure()
		}
		if (play.frames.length === 0) return
		const t = lensTransforms(lensPointAt(sweepTimeAt(elapsed), play.frames))
		if (lensTransform.get() !== t.lens) {
			lensTransform.set(t.lens)
			magTransform.set(t.mag)
			handleTransform.set(t.handle)
		}
	})

	const showLens = lensVisible(phase)
	const showVerified = verifiedShown(phase)
	const checks = checksShown(phase)

	return (
		<div
			ref={rootRef}
			data-scene="fake-statement-lens"
			data-phase={phase}
			aria-hidden="true"
			className={cn(ROOT, '@container relative grid w-full max-w-[420px]')}
		>
			<StatementCard
				ref={cardRef}
				bindRow={bindRow}
				className={cn(
					'z-[1] self-start transition-opacity duration-[400ms]',
					showVerified && 'opacity-0',
				)}
			/>

			<motion.span
				className={cn(
					`${ROOT}__handle`,
					'pointer-events-none absolute left-0 top-0 z-[2] block h-16 w-[14px] origin-top rounded-[7px] bg-tt-ink transition-opacity duration-[400ms]',
					showLens ? 'opacity-100' : 'opacity-0',
				)}
				style={{ transform: handleTransform }}
			/>

			<motion.div
				className={cn(
					`${ROOT}__lens`,
					'pointer-events-none absolute left-0 top-0 z-[3] h-40 w-40 rounded-full bg-white transition-opacity duration-[400ms]',
					showLens ? 'opacity-100' : 'opacity-0',
				)}
				style={{ transform: lensTransform }}
			>
				<div className={cn(`${ROOT}__glass`, 'absolute inset-0 rounded-full')}>
					<motion.div
						ref={magRef}
						className={cn(`${ROOT}__mag`, 'absolute left-0 top-0')}
						style={{ transform: magTransform }}
					>
						<StatementCard flat />
					</motion.div>
				</div>
			</motion.div>

			<VerifiedCard shown={showVerified} checks={checks} />
		</div>
	)
}

const CARD = 'rounded-[22px] bg-white p-5 pb-4 shadow-tt-card'
/* Rows tighten with the card's own width (the root is a size container),
 * so a narrow tile on a tablet gets the same treatment as a small phone. */
const ROW =
	'grid grid-cols-[48px_1fr_auto_18px] items-center gap-2.5 border-b border-tt-line-soft px-0.5 py-2.5 text-[13.5px] last:border-b-0 @max-[340px]:grid-cols-[42px_1fr_auto_16px] @max-[340px]:gap-2 @max-[300px]:grid-cols-[auto_1fr_auto_14px] @max-[300px]:gap-1.5 @max-[300px]:text-[13px]'
/* The statement has no check marks; under 300px its empty last column goes
 * to the label so "Payroll Deposit" stays on one line. */
const STATEMENT_ROW = cn(ROW, '@max-[300px]:grid-cols-[auto_1fr_auto]')
const DATE = 'whitespace-nowrap text-[12px] text-tt-muted'
const AMOUNT = 'tt-tabular font-semibold'

function StatementCard({
	ref,
	flat = false,
	bindRow,
	className,
}: {
	ref?: Ref<HTMLDivElement>
	/** The copy inside the lens: no shadow, no rounding. */
	flat?: boolean
	bindRow?: (
		index: number,
		part: keyof RowElements,
	) => (element: HTMLElement | null) => void
	className?: string
}) {
	return (
		<div
			ref={ref}
			className={cn(
				CARD,
				'[grid-area:1/1]',
				flat && 'rounded-none shadow-none',
				className,
			)}
		>
			<div className="flex items-start justify-between gap-2.5">
				<div>
					<div className="text-[11px] font-medium uppercase tracking-[.14em] text-tt-muted @max-[300px]:text-[10px] @max-[300px]:tracking-[.08em]">
						{STATEMENT.bank}
					</div>
					<div className="mt-1.5 text-[20px] font-semibold tracking-[-.015em] @max-[300px]:text-[18px]">
						{STATEMENT.title}
					</div>
				</div>
				<span className="whitespace-nowrap rounded-lg border border-tt-line px-[9px] py-[5px] text-[11px] font-semibold text-tt-body">
					{STATEMENT.file}
				</span>
			</div>
			<div className="mt-3">
				{STATEMENT.rows.map((row, index) => (
					<div
						key={`${row.date}-${row.label}`}
						ref={bindRow?.(index, 'row')}
						className={STATEMENT_ROW}
					>
						<span className={DATE}>{row.date}</span>
						<span
							ref={bindRow?.(index, 'label')}
							className="justify-self-start"
						>
							{row.label}
						</span>
						<span ref={bindRow?.(index, 'amount')} className={AMOUNT}>
							{row.amount}
						</span>
						<span className="@max-[300px]:hidden" />
					</div>
				))}
			</div>
			<div className="mt-2.5 flex justify-between text-[11.5px] text-tt-muted">
				<span>{STATEMENT.account}</span>
				<span>{STATEMENT.balance}</span>
			</div>
		</div>
	)
}

function VerifiedCard({ shown, checks }: { shown: boolean; checks: number }) {
	return (
		<div
			data-shown={shown || undefined}
			className={cn(
				`${ROOT}__verified`,
				CARD,
				'pointer-events-none z-[4] self-start [grid-area:1/1] max-[900px]:p-4 max-[900px]:pb-3',
			)}
		>
			<div className="flex flex-col items-start gap-2">
				<div className="flex w-full items-center justify-between gap-3">
					<div className="mt-0.5 text-[20px] font-semibold tracking-[-.015em] max-[900px]:text-[18px]">
						{VERIFIED.title}
					</div>
					<img
						src={FLINKS_LOGO}
						alt="Flinks"
						width={409}
						height={155}
						loading="lazy"
						decoding="async"
						className="h-[26px] w-auto flex-none"
					/>
				</div>
				<span className="whitespace-nowrap rounded-lg bg-tt-ok-bg px-[9px] py-[5px] text-[11px] font-semibold text-tt-ok">
					{VERIFIED.chip}
				</span>
			</div>
			<div className="mt-3 max-[900px]:mt-1.5">
				{VERIFIED.rows.map((row: StatementRow, index) => (
					<div
						key={`${row.date}-${row.label}`}
						className={cn(ROW, 'max-[900px]:py-[7px]')}
					>
						<span className={DATE}>{row.date}</span>
						<span>{row.label}</span>
						<span className={AMOUNT}>{row.amount}</span>
						<span
							data-on={index < checks || undefined}
							className={cn(`${ROOT}__check`, 'flex')}
						>
							<CheckIcon className="h-[15px] w-[15px]" />
						</span>
					</div>
				))}
			</div>
		</div>
	)
}
